<template>
  <div :id="`${source}_user_list`" class="flex flex-col gap-4">
    <div v-for="(group, letter) in groupedUsers" :key="`group_${letter}`">
      <div class="text-sm font-semibold px-3 secondary_color">
        {{ letter }}
      </div>

      <div
        v-for="(user, index) in group"
        :key="`${user?.id}_user_${index}`"
        class="flex flex-row items-center justify-between px-2 py-[10px] cursor-pointer"
        @click="handleUserClick(user)"
      >
        <div class="flex flex-row items-center gap-2">
          <ProfileIcon :data="user" :font="16" :size="38" />
          <span class="text-sm primary_color">
            {{ renderEntity(user) }}
          </span>
        </div>
        <a-checkbox
          :checked="isUserSelected(user.id)"
          @click.stop
          @change="(e) => handleCheckboxChange(user, e.target.checked)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from "vue";
import { renderEntity } from "@/src/utils/utils.js";
import { createPlaceholderDM } from "@/src/utils/chatUtils.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";

const isEmbeddedMode = inject("embeddedChatMode", false);
const openEmbeddedChat = inject("openEmbeddedChat", null);

const props = defineProps({
  source: { type: String, required: true },
});

const currentUserId = computed(() => {
  return store.getters["auth/userData"]?.id;
});

const users = computed(() => {
  const allUsers = store.getters["users/users"] || [];
  return allUsers.filter((user) => user.id !== currentUserId.value);
});

const allGroups = computed(() => {
  return store.getters["chats/groups"] || [];
});

const selectedUsers = computed(() => {
  return store.getters["chats/newChatUserList"] || [];
});

const groupedUsers = computed(() => {
  const sorted = [...users.value].sort((a, b) => {
    const nameA = renderEntity(a).toUpperCase();
    const nameB = renderEntity(b).toUpperCase();
    return nameA.localeCompare(nameB);
  });

  const grouped = {};
  sorted.forEach((user) => {
    const firstLetter = renderEntity(user).charAt(0).toUpperCase();
    if (!grouped[firstLetter]) {
      grouped[firstLetter] = [];
    }
    grouped[firstLetter].push(user);
  });

  return grouped;
});

const findExistingDM = (user) => {
  try {
    const userName = renderEntity(user).toLowerCase().trim();

    return allGroups.value.find((group) => {
      if (group.is_dm !== true || (group.type !== "dm" && group.type !== "self_dm") || group.recipient_type !== 1) {
        return false;
      }

      const matchesByName = group.name && group.name.toLowerCase().trim() === userName;

      return matchesByName;
    });
  } catch (error) {
    console.error("An error occurred while finding existing DM: ", error);
    return null;
  }
};

const isUserSelected = (userId) => {
  try {
    return store.getters["chats/isUserSelected"](userId);
  } catch (error) {
    console.error(
      "An error occurred while checking if the user is selected or not: ",
      error,
    );
  }
};

const handleCheckboxChange = (user, checked) => {
  try {
    if (checked) {
      store.dispatch("chats/addUserToChat", user);
    } else {
      store.dispatch("chats/removeUserFromChat", user.id);
    }
  } catch (error) {
    console.error("An error occurred while handling checkbox change: ", error);
  }
};

const handleUserClick = (user) => {
  try {
    if (selectedUsers.value.length === 0) {
      const existingDM = findExistingDM(user);

      if (existingDM) {
        if (isEmbeddedMode && openEmbeddedChat) {
          openEmbeddedChat(existingDM);
          store.dispatch("chats/clearNewChat");
          eventBus.emit("new-message-closed");
        } else {
          store.dispatch("chats/setActiveChat", existingDM);
          store.dispatch("chats/clearNewChat");
          eventBus.emit("new-message-closed");
        }
        return;
      }

      if (isEmbeddedMode && openEmbeddedChat) {
        openEmbeddedChat(createPlaceholderDM(user, renderEntity));
        store.dispatch("chats/clearNewChat");
        eventBus.emit("new-message-closed");
        return;
      }
    }

    const isSelected = isUserSelected(user.id);
    handleCheckboxChange(user, !isSelected);
  } catch (error) {
    console.error("An error occurred while handling user click: ", error);
  }
};
</script>
